import type { Upgrade } from './upgrade-pool';

/**
 * 射速倍率下限 (boss 2026-06-13「攻擊增速太強」)。autoFireMult 越小射越快;
 * 連射卡是乘法疊加,不設下限會指數爆 → 無敵。clamp 在 0.4 = 最快約 2.5× 射速。
 * 純單場戰力 clamp,不碰任何持久/修煉值欄位。
 */
export const AUTO_FIRE_MULT_FLOOR = 0.4;

/**
 * 額外散彈總數上限。spreadBulletBonus 無上限會滿屏彈幕無腦輾。
 * boss 2026-06-17:cap 4→3(再砍一級)。
 */
export const SPREAD_BULLET_CAP = 3;

/**
 * 散彈「副作用」:每發散彈傷害衰減 (boss 2026-06-17「子彈變多有趣但不能太強,要有副作用」)。
 * 之前每發散彈都是全傷 → 滿散彈 ≈ 5× DPS 繞過單發傷害 cap = 太強的真兇。
 * 改成散彈每發只 0.55× 傷害:主前彈仍全傷,散彈用來「打更廣/打群」而非乘倍單體 DPS。
 * 這就是 boss 要的副作用 —— 更多子彈、但每發更弱。
 */
export const SPREAD_DAMAGE_FALLOFF = 0.55;

/**
 * 單發傷害倍率上限 (boss 2026-06-14 第二輪「王都被秒殺、後面不用動就贏」)。
 *
 * 根因 (見 docs/spec/DIFFICULTY_PROGRESSION_MODEL.md §7):重砲(+10/+12 flat)× 暴擊
 * (×2/×3)× 連射 × mastery 是「乘法疊加」,maxed build 的單發傷害是中庸 4 抽的 ~6 倍
 * → 王 HP 照中庸 ~84 DPS 校的 → 強 build 真實 DPS 爆表 → 王 1-2 秒被秒。
 *
 * 解法:把「升級牌帶來的單發傷害倍率」(flat 傷 + 武器等級曲線,**不含** mastery —
 * mastery 是學習軌、合法無上限)封頂在此值。中庸 4 抽 = +10 flat = ×2.67,本就 < cap;
 * 強 build 的 +22 flat × 武器等級 = ×4.4 → 被夾回 2.8。讓 strong/median DPS 比從 ~6×
 * 收斂到 ~2.2×,王不再被秒(strong build tier1 TTK 仍 ~5s,王演得出招)。
 * 純單場戰力 clamp,不碰任何持久/修煉值/學習軌欄位。
 */
export const RUN_DAMAGE_MULT_CAP = 2.8;

/**
 * 平均暴擊倍率上限 (同上,boss 2026-06-14 第二輪)。暴擊機率 × (暴擊倍率-1) 的平均加成
 * 也是乘法疊加的一環:暴擊 35%×2 + 超新星 +20%×3 → 平均 ×1.85;封在 1.6 避免暴擊堆疊
 * 與傷害 cap 疊乘再次爆表。中庸 4 抽 (35%×2 = 平均 ×1.35) 本就 < cap,不受影響。
 */
export const CRIT_AVG_MULT_CAP = 1.6;

export class RunModifiers {
  speedMult = 1;
  autoFireMult = 1;        // smaller = faster
  hpBonus = 0;
  chargeMultBonus = 0;     // added to factionMultiplier
  spreadBulletBonus = 0;
  bulletDamageBonus = 0;
  bulletSizeMult = 1;
  freeXPMult = 1;
  critChance = 0;
  critMult = 1;
  startShieldHits = 0;
  lifestealPerKill = 0;
  slowAuraEnabled = false;

  // ── 質變 (mutation) modifiers ──────────────────────────────────────────────
  // Phaser-free pure data. Default values = NO mutation, so behaviour is
  // identical to today until a 質變 card writes a non-zero value here. The scene
  // reads these flags each frame/collision (same pattern as slowAuraEnabled).
  /** 子彈反彈次數:命中後最多再反彈這麼多次轉向最近的另一隻敵人 (0 = 不反彈). */
  bounceCount = 0;
  /** 命中時 AoE 半徑 (px):>0 時在命中點對半徑內所有敵人造成一次爆炸傷害 (0 = 不爆). */
  explodeOnHitRadius = 0;

  // ── synergy (build combo) 旗標 ─────────────────────────────────────────────
  // 鐵則:synergy 純當局戰力,跟升級卡同性質 (跨場歸零)。由 synergies.ts 的
  // detectSynergies(this) 在「湊齊組合」時寫入;scene 每幀/每碰撞讀來施工額外
  // 效果 (同 slowAuraEnabled / bounceCount 模式)。絕不碰 progress/修煉值。
  // 預設值 = 無 synergy,行為同今日,直到玩家湊出組合。
  /** 連鎖爆炸 (彈跳+爆炸):彈跳落點也爆。false = 無。 */
  synergyChainExplode = false;
  /** 幸運散彈 (散彈+暴擊):額外暴擊機率加成 (0 = 無)。算傷害時 critChance + 此值。 */
  synergyCritBonus = 0;
  /** 疾風連打 (連射+加速):自動射擊間隔再乘此倍率 (1 = 無,越小越快)。 */
  synergyFireMult = 1;
  /** 本場已觸發過的 synergy id 清單 (避免 FX 重跳)。打完即丟。 */
  activeSynergies: string[] = [];

  // ── 場內武器進化 (weapon evolution, R-004 包 C) 旗標 ────────────────────────
  // 鐵則:武器進化純當局質變 — 跟升級卡/synergy 同性質 (跨場 new RunModifiers 歸零)。
  // 由 weapon-evolution.ts 的 detectWeaponEvolutions(this) 在「升級卡疊到門檻 / 集滿
  // 指定兩卡」時寫入;scene 每幀/每碰撞讀來施工強化形態 (同 synergy 旗標模式)。
  // 絕不碰 progress/factionXP/freeXP/skillLevels/永久戰力。預設值 = 未進化,行為同今日。
  /** 本場每張升級卡(依 id)被選的次數。武器進化「同卡疊 N 次」用此判定。打完即丟。 */
  pickCounts: Record<string, number> = {};
  /** 本場已進化的武器 id 清單 (避免 FX/數值重套)。打完即丟。 */
  weaponEvolutions: string[] = [];
  /** 貫穿彈 (piercing):子彈穿透敵人不消失的次數 (0 = 不穿透)。 */
  pierceCount = 0;
  /** 爆裂散彈 (blast-spread):散彈也帶爆炸 — 額外爆炸半徑 (px,0 = 無)。 */
  spreadBlastRadius = 0;
  /** 武器進化額外子彈傷害加成 (整數,0 = 無)。質變數值來自 balance config。 */
  evoBulletDamageBonus = 0;
  /** 武器進化額外彈跳次數 (0 = 無)。 */
  evoBounceBonus = 0;
  /** 武器進化額外散彈數 (0 = 無)。 */
  evoSpreadBonus = 0;

  killCount = 0;  // for lifesteal counter

  // ── 多重武器 / 夥伴 (預留, Phase 4 接 scene 實體) ──────────────────────────
  // Phaser-free 純資料。預設空 = 無副武器,行為同今日。Phase 2 先鋪欄位讓
  // §5.3 資料結構就位(scene 尚未讀取),向後相容。
  /** 副武器 key 清單 (上限 3,去重由施工端負責)。空 = 無副武器。 */
  autoWeapons: string[] = [];

  // ── 局內經濟 (coins) ───────────────────────────────────────────────────────
  // 鐵則(學習與遊戲分離):coins 是純局內貨幣,打完即丟。saveProgress 永不寫
  // coins;coins 與 freeXP/factionXP/修煉值完全無關,不進任何傷害公式。敵人死亡
  // 由 scene 呼 addCoins(常數 × coinDropMult);商店扣 spendCoins。
  /** 本場累積的局內金幣 (打完即丟,不持久化)。 */
  coins = 0;
  /** 金幣掉落倍率 (預設 1)。質變/商品可加成;與修煉值無關。 */
  coinDropMult = 1;

  applyUpgrade(u: Upgrade): void {
    u.apply(this);
    // 記錄「這張卡(依 id)本場被選了幾次」— 場內武器進化「同卡疊 N 次」門檻用。
    // 純當局計數 (跨場 new 歸零),不碰 progress/修煉值。
    this.pickCounts[u.id] = (this.pickCounts[u.id] ?? 0) + 1;
  }

  /** 加金幣 (敵人掉落)。負值/NaN 視為 no-op,確保 HUD 不顯示 NaN。 */
  addCoins(amount: number): void {
    if (!Number.isFinite(amount) || amount <= 0) return;
    this.coins += Math.floor(amount);
  }

  /**
   * 嘗試花費金幣 (商店購買)。買得起→扣款回 true;買不起→不動回 false。
   * 負值/NaN 視為 0 元 (永遠買得起,但不扣)。
   */
  spendCoins(price: number): boolean {
    const p = Number.isFinite(price) && price > 0 ? Math.floor(price) : 0;
    if (this.coins < p) return false;
    this.coins -= p;
    return true;
  }

  /**
   * 套下限 clamp 後的有效射速倍率 (scene 算開火間隔時用,**不直接讀 autoFireMult**)。
   * 連射卡乘法疊加會無限趨近 0 (= 無限快) → 無敵;clamp 在 AUTO_FIRE_MULT_FLOOR
   * 保證最快約 2.5× 射速。原始 autoFireMult 仍可繼續疊 (給 synergy 偵測讀),只在
   * 「實際生效」這一步封頂。NaN/非正值退化為 1 (= 不加速),避免 HUD/間隔算出怪值。
   */
  effectiveAutoFireMult(): number {
    const v = Number.isFinite(this.autoFireMult) && this.autoFireMult > 0 ? this.autoFireMult : 1;
    return Math.max(AUTO_FIRE_MULT_FLOOR, v);
  }

  /**
   * 套上限 cap 後的有效額外散彈總數 (scene 生散彈時用,**不直接加 spreadBulletBonus**)。
   * 含武器進化的 evoSpreadBonus 一起算,cap 在 SPREAD_BULLET_CAP 顆,杜絕滿屏彈幕無腦輾。
   * 負值/NaN 視為 0 (= 無額外散彈)。
   */
  effectiveSpreadBullets(): number {
    const raw = this.spreadBulletBonus + this.evoSpreadBonus;
    if (!Number.isFinite(raw) || raw <= 0) return 0;
    return Math.min(SPREAD_BULLET_CAP, Math.floor(raw));
  }

  /**
   * 升級牌帶來的「單發傷害倍率(相對 base bullet damage)」,封頂在 RUN_DAMAGE_MULT_CAP
   * (boss 2026-06-14 第二輪「王都被秒殺」)。輸入 = 已含 flat 傷與武器等級曲線後的
   * 「over-base 倍率」(= (base + flat) × weaponLevelMult / base);輸出 = clamp 後的倍率。
   *
   * **不含 mastery**:mastery(學習軌)合法無上限,由呼叫端在 cap 之外另乘。
   * NaN/<=0 退化為 1(不加成),避免算出怪值。純當局戰力 clamp,不碰學習軌/修煉值。
   */
  cappedDamageMult(overBaseMult: number): number {
    const v = Number.isFinite(overBaseMult) && overBaseMult > 0 ? overBaseMult : 1;
    return Math.min(RUN_DAMAGE_MULT_CAP, v);
  }

  /**
   * 平均暴擊倍率,封頂在 CRIT_AVG_MULT_CAP(boss 2026-06-14 第二輪)。
   * 用 critChance(+ synergyCritBonus)× (critMult-1) 算「期望加成」再夾上限。
   * 這是給「模型/校準」用的純函式(實戰仍是逐發隨機暴擊);讓 difficulty-model 能用
   * 與實戰一致的封頂值反推王 HP。回 [1, CRIT_AVG_MULT_CAP]。
   */
  cappedCritAvg(): number {
    const chance = Math.max(0, (Number.isFinite(this.critChance) ? this.critChance : 0)
      + (Number.isFinite(this.synergyCritBonus) ? this.synergyCritBonus : 0));
    const mult = Number.isFinite(this.critMult) && this.critMult > 1 ? this.critMult : 1;
    const avg = 1 + Math.min(1, chance) * (mult - 1);
    return Math.min(CRIT_AVG_MULT_CAP, Math.max(1, avg));
  }
}
